import type { Integration, PipelinesConnection } from '@/services/db/integration/entity';
import { ConnectionType, DataFlow, type ConnectionStatus } from '../../catalog/enums';
import { IntegrationIDs } from '../../catalog/ids';
import {
  pipelinesConnectionDataFlow,
  pipelinesConnectionIcon,
  pipelinesConnectionImageSrc,
  pipelinesConnectionIntegrationId,
  pipelinesConnectionType,
} from '../../derived';
import { sortIntegrations, visibleIntegrationsIndex } from '../../directory/filter-sort-group';
import type { SynthesizedIntegration } from '../../directory/types';
import { createJourneysHref } from './journeys-href';
import {
  parseCustomForms,
  parseJotForms,
  parseJourneysIntegration,
  parseReportingWebhooks,
  parseTypeForms,
} from './parsers';
import { sortByStatusAndName } from './sort';
import { ProductApp, type ConnectionSources, type ConnectionsConfig, type IntegrationConnection } from './types';

/**
 * Journeys integration ids excluded from the parsed-integration pass — forms
 * and reporting webhooks are added separately, Segment-in is not a connection,
 * and youtube/instagram are folded into their ad-network parents.
 */
const FILTERED_JOURNEYS_INTEGRATIONS = new Set<string>([
  IntegrationIDs.segmentIn,
  IntegrationIDs.customForms,
  IntegrationIDs.jotform,
  IntegrationIDs.typeform,
  IntegrationIDs.reportingWebhooks,
  IntegrationIDs.youtube,
  IntegrationIDs.instagram,
]);

export interface BuildConnectionsParams {
  /** The full synthesized integrations list (the directory's `buildIntegrationsList` output). */
  synthesizedIntegrations: readonly SynthesizedIntegration[];
  /** The raw pipelines-connection wire records. */
  pipelinesConnections: readonly PipelinesConnection[];
  /** The backend catalog list, for the pipelines connection → integration join. */
  catalogIntegrations: readonly Integration[];
  sources: ConnectionSources;
  config: ConnectionsConfig;
}

/** Normalize a pipelines-connection wire record into an `IntegrationConnection`. */
function normalizePipelinesConnection(
  connection: PipelinesConnection,
  catalogIntegrations: readonly Integration[],
): IntegrationConnection {
  const integrationModel = catalogIntegrations.find(
    (integration) => integration.id === pipelinesConnectionIntegrationId(connection),
  );
  return {
    id: connection.id,
    type: connection.type,
    name: connection.name,
    icon: pipelinesConnectionIcon(integrationModel),
    imageSrc: pipelinesConnectionImageSrc(connection, integrationModel),
    dataFlow: pipelinesConnectionDataFlow(connection),
    product: ProductApp.dataPipelines,
    href: connection.path,
    path: connection.path,
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    status: connection.status as ConnectionStatus,
    statusMessage: connection.status_message,
    createdAt: connection.created_at,
    lastActivity: connection.last_activity,
    // Ember declares this `@attr('array')`, and that transform returns `[]` for any non-array
    // (ui/app/transforms/array.ts), so `connectedTo` is never null there. Copying the raw field
    // dropped that: a null or omitted `connected_to` became undefined, and group-connections
    // gates its add control on `connectedTo != null` — so the Connections cell rendered empty.
    connectedTo: connection.connected_to ?? [],
    direction: connection.direction,
    isAutoTrack: connection.is_auto_track,
    integrationName: integrationModel?.name,
  };
}

/**
 * Every pipelines connection keyed by its wire id, for the Connections column's
 * related-connection lookup (`connectedTo` holds the numeric ids of connections on
 * the opposite side of the flow).
 *
 * Deliberately built from the *unfiltered* records: Ember reads these back out of
 * the Ember Data store, which `store.findAll` fills with every fetched connection —
 * the auto-track filter only trims the visible list, so a hidden auto-track source
 * is still resolvable as somebody else's related connection.
 */
export function buildPipelinesConnectionsById(
  pipelinesConnections: readonly PipelinesConnection[],
  catalogIntegrations: readonly Integration[],
): Map<string, IntegrationConnection> {
  return new Map(
    pipelinesConnections.map((connection) => [
      connection.id,
      normalizePipelinesConnection(connection, catalogIntegrations),
    ]),
  );
}

/**
 * Auto-track sources are dropped unless auto-track/passthrough is enabled (and
 * then only the data-in ones) — they would otherwise show as connections with
 * no data flowing. Ported from the Ember `fetchPipelinesConnections` filter.
 */
function isVisiblePipelinesConnection(connection: PipelinesConnection, areAutoTrackSourcesEnabled: boolean): boolean {
  if (connection.is_auto_track) {
    return areAutoTrackSourcesEnabled && pipelinesConnectionDataFlow(connection) === DataFlow.in;
  }
  return true;
}

/** The pipelines half of the list. */
function buildPipelinesConnections(params: BuildConnectionsParams): IntegrationConnection[] {
  return params.pipelinesConnections
    .filter((connection) => isVisiblePipelinesConnection(connection, params.config.areAutoTrackSourcesEnabled))
    .map((connection) => normalizePipelinesConnection(connection, params.catalogIntegrations));
}

export interface BuildDeveloperConnectionsParams {
  /** The raw pipelines-connection wire records. */
  pipelinesConnections: readonly PipelinesConnection[];
  /** The backend catalog list, for the pipelines connection → integration join. */
  catalogIntegrations: readonly Integration[];
  /** `cdp_track_enabled` OR `cdp_passthrough_enabled`. */
  areAutoTrackSourcesEnabled: boolean;
}

/**
 * The Developers sub-tab's connection list: pipelines connections only (no
 * Journeys-side connections), restricted to `Developer`-type integrations via
 * the catalog join, with the same auto-track visibility rule and sort as the
 * Connections tab. Pure — all inputs are explicit.
 */
export function buildDeveloperConnectionsList(params: BuildDeveloperConnectionsParams): IntegrationConnection[] {
  return params.pipelinesConnections
    .filter((connection) => isVisiblePipelinesConnection(connection, params.areAutoTrackSourcesEnabled))
    .filter((connection) => {
      const integrationModel = params.catalogIntegrations.find(
        (integration) => integration.id === pipelinesConnectionIntegrationId(connection),
      );
      return pipelinesConnectionType(integrationModel) === ConnectionType.developer;
    })
    .map((connection) => normalizePipelinesConnection(connection, params.catalogIntegrations))
    .sort(sortByStatusAndName);
}

/**
 * The Journeys half of the list: enabled, non-excluded synthesized entries run
 * through their parsers, plus the separately-parsed forms and reporting
 * webhooks, each given its resolved `href`. Ported from the Ember overview
 * controller's `journeysConnections`.
 */
function buildJourneysConnections(params: BuildConnectionsParams): IntegrationConnection[] {
  const { synthesizedIntegrations, sources, config } = params;
  const enabled = sortIntegrations(visibleIntegrationsIndex(synthesizedIntegrations)).filter(
    (integration) => integration.enabled === true && !FILTERED_JOURNEYS_INTEGRATIONS.has(integration.id),
  );
  const fromIntegrations = enabled.flatMap((integration) => parseJourneysIntegration(integration, sources, config));
  const fromForms = [
    ...parseCustomForms(sources.forms),
    ...parseTypeForms(sources.forms),
    ...parseJotForms(sources.forms),
  ];
  const fromWebhooks = parseReportingWebhooks(sources);
  return [...fromIntegrations, ...fromForms, ...fromWebhooks].map((connection) => ({
    ...connection,
    href: createJourneysHref(connection, config.workspaceId),
  }));
}

/**
 * Build the Connections tab's flat, sorted `IntegrationConnection` list from the
 * synthesized integrations, the overview-only data sources, and the pipelines
 * connections. Pure — all inputs are explicit.
 */
export function buildConnectionsList(params: BuildConnectionsParams): IntegrationConnection[] {
  const journeys = buildJourneysConnections(params);
  const pipelines = buildPipelinesConnections(params);
  return [...journeys, ...pipelines].sort(sortByStatusAndName);
}
